// 博客服务
export interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string; // 摘要
  author: string;
  publishedAt: string;
  updatedAt: string;
  status: 'draft' | 'published' | 'archived';
  tags: string[];
  coverImage?: string; // 封面图片URL
  readTime: number; // 预估阅读时间（分钟）
  views: number; // 阅读量
}

export interface BlogStats {
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  totalViews: number;
  totalTags: string[];
}

// 检查是否为生产环境
const isProduction = process.env.NODE_ENV === 'production';

// API基础URL
const API_BASE = isProduction ? '' : '';

class BlogService {
  private readonly STORAGE_KEY = 'blog_posts';
  private readonly STATS_KEY = 'blog_stats';

  // 获取所有博客文章
  async getAllPosts(): Promise<BlogPost[]> {
    if (!isProduction) {
      // 开发环境使用localStorage
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) return [];
        return JSON.parse(stored);
      } catch (error) {
        console.error('获取博客文章失败:', error);
        return [];
      }
    }

    // 生产环境使用API
    try {
      const response = await fetch(`${API_BASE}/api/blog`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('获取博客文章失败');
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('获取博客文章失败:', error);
      return [];
    }
  }

  // 获取已发布的博客文章
  async getPublishedPosts(): Promise<BlogPost[]> {
    const allPosts = await this.getAllPosts();
    return allPosts
      .filter(post => post.status === 'published')
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }

  // 根据ID获取博客文章
  async getPostById(id: string): Promise<BlogPost | null> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const posts = await this.getAllPosts();
      return posts.find(post => post.id === id) || null;
    }

    // 生产环境使用API
    try {
      const response = await fetch(`${API_BASE}/api/blog/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('获取博客文章失败');
      }

      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      console.error('获取博客文章失败:', error);
      return null;
    }
  }

  // 保存博客文章
  async savePost(post: BlogPost): Promise<boolean> {
    if (!isProduction) {
      // 开发环境使用localStorage
      try {
        const posts = await this.getAllPosts();
        const existingIndex = posts.findIndex(p => p.id === post.id);
        
        if (existingIndex >= 0) {
          posts[existingIndex] = { ...post, updatedAt: new Date().toISOString() };
        } else {
          // 如果是新文章且没有ID，生成一个新的ID
          if (!post.id || post.id === '') {
            post.id = this.generateId();
          }
          posts.push(post);
        }
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(posts));
        await this.updateStats();
        return true;
      } catch (error) {
        console.error('保存博客文章失败:', error);
        return false;
      }
    }

    // 生产环境使用API
    try {
      const response = await fetch(`${API_BASE}/api/blog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ post }),
      });

      if (!response.ok) {
        throw new Error('保存博客文章失败');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('保存博客文章失败:', error);
      return false;
    }
  }

  // 删除博客文章
  async deletePost(id: string): Promise<boolean> {
    if (!isProduction) {
      // 开发环境使用localStorage
      try {
        const posts = await this.getAllPosts();
        const filteredPosts = posts.filter(post => post.id !== id);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredPosts));
        await this.updateStats();
        return true;
      } catch (error) {
        console.error('删除博客文章失败:', error);
        return false;
      }
    }

    // 生产环境使用API
    try {
      const response = await fetch(`${API_BASE}/api/blog/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('删除博客文章失败');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('删除博客文章失败:', error);
      return false;
    }
  }

  // 增加阅读量
  async incrementViews(id: string): Promise<void> {
    if (!isProduction) {
      // 开发环境使用localStorage
      try {
        const posts = await this.getAllPosts();
        const postIndex = posts.findIndex(post => post.id === id);
        if (postIndex >= 0) {
          posts[postIndex].views += 1;
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(posts));
          await this.updateStats();
        }
      } catch (error) {
        console.error('更新阅读量失败:', error);
      }
    } else {
      // 生产环境使用API
      try {
        await fetch(`${API_BASE}/api/blog/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            postId: id, 
            updates: { views: 'increment' } // 特殊标记表示增加阅读量
          }),
        });
      } catch (error) {
        console.error('更新阅读量失败:', error);
      }
    }
  }

  // 获取统计信息
  async getStats(): Promise<BlogStats> {
    const posts = await this.getAllPosts();
    const publishedPosts = posts.filter(post => post.status === 'published');
    const draftPosts = posts.filter(post => post.status === 'draft');
    const totalViews = posts.reduce((sum, post) => sum + post.views, 0);
    const allTags = [...new Set(posts.flatMap(post => post.tags))];

    return {
      totalPosts: posts.length,
      publishedPosts: publishedPosts.length,
      draftPosts: draftPosts.length,
      totalViews,
      totalTags: allTags
    };
  }

  // 更新统计信息（仅开发环境需要）
  private async updateStats(): Promise<void> {
    if (!isProduction) {
      const stats = await this.getStats();
      localStorage.setItem(this.STATS_KEY, JSON.stringify(stats));
    }
  }

  // 计算阅读时间
  calculateReadTime(content: string): number {
    const wordsPerMinute = 200; // 假设每分钟阅读200字
    const wordCount = content.length;
    return Math.ceil(wordCount / wordsPerMinute);
  }

  // 生成摘要
  generateExcerpt(content: string, maxLength: number = 150): string {
    const plainText = content.replace(/<[^>]*>/g, ''); // 移除HTML标签
    return plainText.length > maxLength 
      ? plainText.substring(0, maxLength) + '...' 
      : plainText;
  }

  // 生成ID
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // 搜索文章
  async searchPosts(query: string): Promise<BlogPost[]> {
    const posts = await this.getPublishedPosts();
    const lowercaseQuery = query.toLowerCase();
    
    return posts.filter((post: BlogPost) => 
      post.title.toLowerCase().includes(lowercaseQuery) ||
      post.content.toLowerCase().includes(lowercaseQuery) ||
      post.tags.some((tag: string) => tag.toLowerCase().includes(lowercaseQuery))
    );
  }

  // 根据标签获取文章
  async getPostsByTag(tag: string): Promise<BlogPost[]> {
    const posts = await this.getPublishedPosts();
    return posts.filter(post => post.tags.includes(tag));
  }
}

export const blogService = new BlogService();
export { BlogService };